import { Controller } from 'react-hook-form';
import type { Control, UseFormClearErrors } from 'react-hook-form';

import { Stack } from 'ui/stack';
import { Input } from 'ui/input';
import { Select } from 'ui/select';
import { TextArea } from 'ui/text-area';

export interface ProductFormValues {
  name: string;
  description: string;
  type: string;
}

export interface ProductTypeOption {
  name: string;
  description: string;
}

interface ProductFormProps {
  control: Control<ProductFormValues>;
  disabled: boolean;
  productTypes: ProductTypeOption[];
  clearErrors: UseFormClearErrors<ProductFormValues>;
}

const ProductForm = ({
  control,
  disabled,
  productTypes,
  clearErrors,
}: ProductFormProps): React.JSX.Element => (
  <Stack
    direction="row"
    sx={{ gap: 4, height: '100%', alignItems: 'baseline' }}
  >
    <Stack sx={{ gap: 1.5, width: '50%' }}>
      <Controller
        name="name"
        control={control}
        rules={{ required: 'This field is required' }}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            label="Product name"
            placeholder="Product name..."
            disabled={disabled}
            error={!!error}
            errorMessage={error?.message}
            required={true}
            sx={{ mt: 1.5 }}
            data-testid="product-name-input"
          />
        )}
      />
      <Controller
        name="type"
        control={control}
        rules={{ required: 'This field is required' }}
        render={({ field, fieldState: { error } }) => {
          const transformedOptions = productTypes.map((pt) => ({
            value: pt.name,
            label: pt.description,
          }));

          return (
            <Select
              {...field}
              label="Product type"
              disabled={disabled}
              options={transformedOptions}
              error={!!error}
              errorMessage={error && 'This field is required'}
              required
              sx={{ mt: 1.5 }}
              onChange={(selectedOption) => {
                field.onChange(
                  (selectedOption as { value: string } | null)?.value
                );
              }}
              value={field.value}
              clearInputErr={() => clearErrors('type')}
              data-testid="product-type-select"
            />
          );
        }}
      />
    </Stack>

    <Stack sx={{ gap: 1.5, width: '50%' }}>
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <TextArea
            {...field}
            label="Product description"
            placeholder="Product description..."
            disabled={disabled}
            rows={5}
            data-testid="product-description-textarea"
          />
        )}
      />
    </Stack>
  </Stack>
);

export default ProductForm;
